import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Facebook, Music, Image, Upload, ChevronRight, Check,
  AlertCircle, Plug, Activity
} from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import api from "@/lib/api";

function ConnectionCard({ icon: Icon, name, description, connected, configured, color, path, onConnect }) {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate(path)}
      className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-white/15 transition-all cursor-pointer group"
      data-testid={`connection-card-${name.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-5">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center`} style={{ background: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        {connected ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
            <Check className="w-3 h-3" /> Connected
          </span>
        ) : !configured ? (
          <span className="flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full">
            <AlertCircle className="w-3 h-3" /> Setup Required
          </span>
        ) : null}
      </div>
      <h3 className="text-base font-semibold text-white mb-1.5" style={{ fontFamily: 'Manrope' }}>{name}</h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-4">{description}</p>
      <div className="flex items-center text-xs text-slate-500 group-hover:text-[#00FF88] transition-colors">
        {connected ? "View & Manage" : "Connect Now"} <ChevronRight className="w-3 h-3 ml-1" />
      </div>
    </motion.div>
  );
}

export default function ConnectionsPage() {
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/facebook/status").catch(() => ({ data: {} })),
      api.get("/spotify/status").catch(() => ({ data: {} })),
      api.get("/oura/status").catch(() => ({ data: {} })),
    ]).then(([fb, sp, oura]) => {
      setStatuses({
        facebook: { connected: fb.data.connected, configured: fb.data.fb_configured },
        spotify: { connected: sp.data.connected, configured: sp.data.configured },
        oura: { connected: oura.data.connected, configured: oura.data.configured },
      });
    });
  }, []);

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-5xl mx-auto" data-testid="connections-page">
      <div className="mb-10">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Integrations</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Connections
        </h1>
        <p className="text-sm text-slate-400 mt-1">Import your memories and connect your digital life</p>
      </div>

      {/* Service Connections */}
      <div className="mb-10">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Services</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <ConnectionCard
            icon={Activity}
            name="Oura Ring"
            description="Sync your health data — sleep, activity, heart rate, and readiness scores."
            connected={statuses.oura?.connected}
            configured={statuses.oura?.configured !== false}
            color="#FF6B6B"
            path="/connections/oura"
          />
          <ConnectionCard
            icon={Music}
            name="Spotify"
            description="Connect your music taste — top artists, tracks, genres, and listening history."
            connected={statuses.spotify?.connected}
            configured={statuses.spotify?.configured !== false}
            color="#1DB954"
            path="/connections/spotify"
          />
          <ConnectionCard
            icon={Facebook}
            name="Facebook"
            description="Import your photos and memories from Facebook into your vault."
            connected={statuses.facebook?.connected}
            configured={statuses.facebook?.configured !== false}
            color="#1877F2"
            path="/facebook"
          />
        </div>
      </div>

      {/* Data Import */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Data Import</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConnectionCard
            icon={Facebook}
            name="Facebook Export"
            description="Upload your Facebook data download ZIP to import all your photos and posts."
            connected={false}
            configured={true}
            color="#1877F2"
            path="/connections/import"
          />
          <ConnectionCard
            icon={Image}
            name="Instagram Export"
            description="Upload your Instagram data export to import photos, stories, and reels."
            connected={false}
            configured={true}
            color="#E1306C"
            path="/connections/import"
          />
          <ConnectionCard
            icon={Image}
            name="Google Photos"
            description="Upload your Google Takeout archive to import photos and albums."
            connected={false}
            configured={true}
            color="#4285F4"
            path="/connections/import"
          />
        </div>
      </div>
    </motion.div>
  );
}
